// AddListingForm.tsx
import React, { useState, useRef } from "react";
import { myMutation } from "@/hooks/myMutation";
import { useTransitDialog } from "@/context/TransitDialogContext";
import { MY_ENDPOINTS } from "@raashidh/shared";
import { MyMultiStepFormBuilder } from "@/MY_COMPONENTS/MY_FORM/MyMultiStepFormBuilder";
import { BuildingIcon, Image, LocateIcon, CreditCard, FileText, Settings, Package } from "lucide-react";
import MyRow from "@/components/MyRow";
import MyHeaderLabel from "@/components/MyHeaderLabel";
import { getIslandsMvList } from "@raashidh/shared";

// Property type constants (keep all your existing constants)
const PROPERTY_CATEGORIES = [
  { value: "residential", label: "Residential" },
  { value: "commercial", label: "Commercial" }
];

const PROPERTY_TYPES = {
  residential: [
    { value: "apartment", label: "Apartment" },
    { value: "room", label: "Room" },
    { value: "villa", label: "Villa" },
    { value: "house", label: "House" },
    { value: "land", label: "Land" }
  ],
  commercial: [
    { value: "office", label: "Office" },
    { value: "shop", label: "Shop" },
    { value: "commercial", label: "Commercial Space" }
  ]
};

const PROPERTY_STATUS = [
  { value: "available", label: "Available" },
  { value: "rented", label: "Rented" },
  { value: "maintenance", label: "Maintenance" },
  { value: "coming_soon", label: "Coming Soon" },
  { value: "sold", label: "Sold" }
];

const FURNISHED_STATUS = [
  { value: "unfurnished", label: "Unfurnished" },
  { value: "semi-furnished", label: "Semi-furnished" },
  { value: "fully-furnished", label: "Fully-furnished" }
];

const PRICE_UNITS = [
  { value: "total", label: "Total" },
  { value: "item", label: "Per Item" },
  { value: "night", label: "Per Night" },
  { value: "day", label: "Per Day" },
  { value: "hour", label: "Per Hour" }
];

const PRODUCT_TYPES = [
  { value: "physical", label: "Physical Product" },
  { value: "digital", label: "Digital Product" },
  { value: "service", label: "Service" },
  { value: "rental", label: "Rental" }
];

const AMENITIES = [
  "Elevator", "Kitchen Access", "Laundry Access", "Balcony Access",
  "CCTV", "Water Heater", "Pool", "Gym", "Security", "Parking",
  "Central AC", "WiFi", "Smart TV", "Fridge", "Oven", "Microwave",
  "Dishwasher", "Washer/Dryer", "Furnished", "Private Entrance"
];

// Define the form schema - REMOVED onEnterApi from first step
export const addPropertySchema = {
  steps: [
    {
      id: "basic_info",
      icon: <BuildingIcon className="w-5 h-5" />,
      label: "Basic Information",
      fields: [
        { 
          label: "Product Type", 
          name: "type", 
          required: true, 
          type: "selectbuttons", 
          options: PRODUCT_TYPES,
          defaultValue: "rental"
        },
        { 
          label: "Property Category", 
          name: "property_category", 
          required: true, 
          type: "selectbuttons", 
          options: PROPERTY_CATEGORIES,
          defaultValue: "residential"
        },
        { 
          label: "Property Type", 
          name: "property_type", 
          type: "selectbuttons", 
          dependsOn: "property_category",
          visibleWhen: { field: "property_category", notEmpty: true },
          optionsMap: PROPERTY_TYPES,
          required: true 
        },
        { 
          label: "Property Status", 
          name: "property_status", 
          type: "selectbuttons", 
          options: PROPERTY_STATUS, 
          required: true,
          defaultValue: "available"
        },
        { 
          label: "Listing Title", 
          name: "title", 
          required: true, 
          type: "text",
          placeholder: "e.g., Spacious 2BR Apartment in Hulhumalé"
        },
        { 
          label: "SKU (Unique Identifier)", 
          name: "sku", 
          required: true, 
          type: "text",
          placeholder: "e.g., APT-HULH-001"
        },
        { 
          label: "Slug (URL-friendly name)", 
          name: "slug", 
          required: true, 
          type: "text",
          placeholder: "e.g., spacious-2br-apartment-hulhumale",
          helperText: "Will be auto-generated from title if left empty"
        },
        { 
          label: "Brand/Company Name", 
          name: "brand", 
          type: "text",
          placeholder: "Your company name"
        }
      ]
      // REMOVED: onEnterApi - don't create product yet
    },

    {
      id: "location",
      icon: <LocateIcon className="w-5 h-5" />,
      label: "Location",
      fields: [
        { 
          label: "Country", 
          name: "country", 
          type: "searchableselect", 
          options: [{ value: "MV", label: "Maldives" }], 
          required: true,
          defaultValue: "MV"
        },
        { 
          label: "Island", 
          name: "island", 
          type: "searchableselect", 
          dependsOn: "country",
          visibleWhen: { field: "country", equals: "MV" },
          optionsMap: { MV: getIslandsMvList() },
          required: true,
          placeholder: "Select Island" 
        },
        { 
          label: "City / Ward", 
          name: "city", 
          type: "text",
          placeholder: "e.g., Henveiru"
        },
        { 
          label: "Address Line 1", 
          name: "address_line1", 
          required: true, 
          type: "text",
          placeholder: "Street address"
        },
        { 
          label: "Address Line 2", 
          name: "address_line2", 
          type: "text",
          placeholder: "Apartment, suite, unit, etc."
        },
        { 
          label: "Postal Code", 
          name: "postal_code", 
          type: "text" 
        },
        { 
          label: "Latitude", 
          name: "latitude", 
          type: "number", 
          step: 0.0001, 
          disabled: true,
          visibleWhen: { field: "location", notEmpty: true }
        },
        { 
          label: "Longitude", 
          name: "longitude", 
          type: "number", 
          step: 0.0001, 
          disabled: true,
          visibleWhen: { field: "location", notEmpty: true }
        },
        { 
          label: "Location on Map", 
          name: "location", 
          type: "maplocation", 
          height: 400,
          latField: "latitude",
          lngField: "longitude"
        }
      ]
    },

    {
      id: "details",
      icon: <FileText className="w-5 h-5" />,
      label: "Property Details",
      fields: [
        { 
          label: "Description", 
          name: "description", 
          required: true, 
          type: "tiptap" 
        },
        { 
          label: "Bedrooms", 
          name: "bedrooms", 
          type: "integer", 
          placeholder: "Number of bedrooms" 
        },
        { 
          label: "Bathrooms", 
          name: "bathrooms", 
          type: "number", 
          step: 0.5,
          placeholder: "Number of bathrooms" 
        },
        { 
          label: "Area (sq ft)", 
          name: "area_sqft", 
          type: "integer", 
          placeholder: "Area in square feet" 
        },
        { 
          label: "Area (sq m)", 
          name: "area_sqm", 
          type: "integer", 
          placeholder: "Area in square meters" 
        },
        { 
          label: "Floor Number", 
          name: "floor_number", 
          type: "integer", 
          placeholder: "Floor number" 
        },
        { 
          label: "Total Floors", 
          name: "total_floors", 
          type: "integer", 
          placeholder: "Total floors in building" 
        },
        { 
          label: "Furnished Status", 
          name: "furnished", 
          type: "selectbuttons", 
          options: FURNISHED_STATUS, 
          required: true,
          defaultValue: "unfurnished"
        },
        { 
          label: "Parking Spots", 
          name: "parking_spots", 
          type: "integer", 
          defaultValue: 0 
        },
        { 
          label: "Year Built", 
          name: "year_built", 
          type: "integer", 
          placeholder: "Construction year" 
        },
        { 
          label: "Pet Friendly", 
          name: "pet_friendly", 
          type: "boolean", 
          defaultValue: false 
        },
        { 
          label: "Immediately Available", 
          name: "immediate_available", 
          type: "boolean", 
          defaultValue: true 
        },
        { 
          label: "Utilities Included", 
          name: "utilities_included", 
          type: "boolean", 
          defaultValue: false 
        },
        { 
          label: "Minimum Lease (months)", 
          name: "minimum_lease_months", 
          type: "integer",
          visibleWhen: { field: "property_category", equals: "residential" }
        },
        { 
          label: "Maximum Lease (months)", 
          name: "maximum_lease_months", 
          type: "integer",
          visibleWhen: { field: "property_category", equals: "residential" }
        },
        { 
          label: "Deposit Amount", 
          name: "deposit_amount", 
          type: "number",
          placeholder: "Security deposit amount"
        },
        { 
          label: "Maintenance Fee (Monthly)", 
          name: "maintenance_fee", 
          type: "number"
        },
        { 
          label: "Amenities", 
          name: "amenities", 
          type: "selectbuttons",
          multiple: true,
          options: AMENITIES.map(a => ({ value: a, label: a }))
        },
        { 
          label: "Virtual Tour URL", 
          name: "virtual_tour_url", 
          type: "text",
          placeholder: "https://..."
        }
      ]
    },

    {
      id: "images",
      icon: <Image className="w-5 h-5" />,
      label: "Photos",
      fields: [
        {
          label: "Property Summary",
          name: "property_display",
          type: "display",
          readOnly: true,
          renderCustom: (data: any) => (
            <div className="text-sm font-medium text-gray-700 mb-2">
              <MyHeaderLabel label={data.title || "New Property"} />
              <MyRow label="Property ID: " label2={data.product_id} />
              <MyRow label="Type: " label2={`${data.property_category} - ${data.property_type}`} />
              <MyRow label="Location: " label2={`${data.island}, ${data.city || ""}`} />
            </div>
          ),
        },
        {
          label: "Main Image",
          name: "image_url",
          type: "uploader",
          required: true,
          renameTemplate: "property_{product_id}_main_{timestamp}.jpg",
          accept: "image/*"
        },
        {
          label: "Gallery Images",
          name: "gallery",
          type: "uploader",
          multiple: true,
          renameTemplate: "property_{product_id}_gallery_{index}.jpg",
          accept: "image/*",
          helperText: "Upload multiple images. First image will be used as main image if not set above."
        }
      ]
    },

    {
      id: "pricing",
      icon: <CreditCard className="w-5 h-5" />,
      label: "Pricing & Rules",
      fields: [
        { 
          label: "Price", 
          name: "price", 
          required: true, 
          type: "number" 
        },
        { 
          label: "Price Unit", 
          name: "price_unit", 
          type: "selectbuttons", 
          options: PRICE_UNITS, 
          required: true,
          defaultValue: "night"
        },
        { 
          label: "Sale Price", 
          name: "sale_price", 
          type: "number",
          placeholder: "Discounted price (optional)"
        },
        { 
          label: "Cost Price", 
          name: "cost_price", 
          type: "number",
          placeholder: "Your cost (for analytics)"
        },
        { 
          label: "Weight (kg)", 
          name: "weight", 
          type: "number",
          step: 0.01,
          placeholder: "For physical products",
          visibleWhen: { field: "type", equals: "physical" }
        },
        { 
          label: "Dimensions (LxWxH)", 
          name: "dimensions", 
          type: "text",
          placeholder: "e.g., 10x20x30 cm",
          visibleWhen: { field: "type", equals: "physical" }
        },
        { 
          label: "Check-in Time", 
          name: "check_in_time", 
          type: "text",
          placeholder: "14:00",
          defaultValue: "14:00",
          visibleWhen: { field: "type", equals: "rental" }
        },
        { 
          label: "Check-out Time", 
          name: "check_out_time", 
          type: "text",
          placeholder: "11:00",
          defaultValue: "11:00",
          visibleWhen: { field: "type", equals: "rental" }
        },
        { 
          label: "Minimum Stay (nights)", 
          name: "min_stay", 
          type: "integer",
          defaultValue: 1,
          visibleWhen: { field: "type", equals: "rental" }
        },
        { 
          label: "Maximum Stay (nights)", 
          name: "max_stay", 
          type: "integer",
          visibleWhen: { field: "type", equals: "rental" }
        },
        { 
          label: "Feature this listing", 
          name: "is_featured", 
          type: "boolean", 
          defaultValue: false 
        },
        { 
          label: "Mark as New", 
          name: "is_new", 
          type: "boolean", 
          defaultValue: true 
        }
      ]
    },

    {
      id: "inventory",
      icon: <Package className="w-5 h-5" />,
      label: "Inventory",
      fields: [
        { 
          label: "Stock Quantity", 
          name: "stock", 
          type: "integer", 
          required: true,
          defaultValue: 1 
        },
        { 
          label: "Reserved Quantity", 
          name: "reserved", 
          type: "integer",
          defaultValue: 0,
          helperText: "Number of items reserved but not yet sold"
        },
        { 
          label: "Low Stock Threshold", 
          name: "low_stock_threshold", 
          type: "integer",
          defaultValue: 5 
        },
        { 
          label: "Reorder Point", 
          name: "reorder_point", 
          type: "integer",
          defaultValue: 10 
        }
      ]
    }
  ],
};

export default function AddListingForm({ onSuccess, onClose }: { onSuccess?: (property: any) => void; onClose?: () => void }) {
  const formMethodsRef = useRef<any>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const transitDialog = useTransitDialog();

  // Create property mutation
  const createProperty = myMutation({
    endpoint: MY_ENDPOINTS.PRODUCT_ENDPOINTS.CREATE_PRODUCT.path,
    method: "POST",
    onSuccess: (res: any) => {
      transitDialog.showSuccess("Property created successfully 🎉");
      if (onSuccess) onSuccess(res);
      if (onClose) onClose();
    },
    onError: (error: any) => {
      transitDialog.showError("Failed to create property: " + (error.message || "Unknown error"));
      console.error("Create property error:", error);
    }
  });

  // Helper function to generate slug from title
  const generateSlug = (title: string): string => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  function handleFinalSubmit(data: any) {
    console.log("Final submit data:", data);
    
    // Auto-generate slug from title if not provided
    if (!data.slug && data.title) {
      data.slug = generateSlug(data.title);
    }
    
    // Validate required fields for creation
    if (!data.title) {
      transitDialog.showError("Title is required");
      return;
    }
    
    if (!data.sku) {
      transitDialog.showError("SKU is required");
      return;
    }
    
    if (!data.slug) {
      transitDialog.showError("Slug is required");
      return;
    }
    
    if (!data.price || data.price <= 0) {
      transitDialog.showError("Valid price is required");
      return;
    }
    
    // Prepare the payload with proper data types
    const payload = {
      // Basic info
      title: String(data.title),
      sku: String(data.sku),
      slug: String(data.slug),
      description: data.description || null,
      brand: data.brand || null,
      
      // Pricing
      price: Number(data.price),
      price_unit: data.price_unit || "night",
      sale_price: data.sale_price ? Number(data.sale_price) : null,
      cost_price: data.cost_price ? Number(data.cost_price) : null,
      
      // Inventory
      stock: Number(data.stock) || 1,
      reserved: Number(data.reserved) || 0,
      low_stock_threshold: Number(data.low_stock_threshold) || 5,
      reorder_point: Number(data.reorder_point) || 10,
      
      // Product type
      type: data.type || "rental",
      attributes: data.attributes ? JSON.stringify(data.attributes) : null,
      
      // Media
      image_url: data.image_url instanceof File ? null : (data.image_url || null),
      gallery: data.gallery || null,
      
      // Booking rules
      booking_rules: data.type === 'rental' ? JSON.stringify({
        check_in_time: data.check_in_time || "14:00",
        check_out_time: data.check_out_time || "11:00",
        min_stay: data.min_stay || 1,
        max_stay: data.max_stay || null
      }) : null,
      
      // Physical attributes
      weight: data.weight ? Number(data.weight) : null,
      dimensions: data.dimensions || null,
      
      // Property-specific fields
      property_category: data.property_category || "residential",
      property_type: data.property_type || "apartment",
      property_status: data.property_status || "available",
      bedrooms: data.bedrooms ? Number(data.bedrooms) : null,
      bathrooms: data.bathrooms ? Number(data.bathrooms) : null,
      area_sqft: data.area_sqft ? Number(data.area_sqft) : null,
      area_sqm: data.area_sqm ? Number(data.area_sqm) : null,
      floor_number: data.floor_number ? Number(data.floor_number) : null,
      total_floors: data.total_floors ? Number(data.total_floors) : null,
      year_built: data.year_built ? Number(data.year_built) : null,
      furnished: data.furnished || "unfurnished",
      parking_spots: Number(data.parking_spots) || 0,
      pet_friendly: data.pet_friendly === true ? 1 : 0,
      immediate_available: data.immediate_available === true ? 1 : 0,
      utilities_included: data.utilities_included === true ? 1 : 0,
      minimum_lease_months: data.minimum_lease_months ? Number(data.minimum_lease_months) : null,
      maximum_lease_months: data.maximum_lease_months ? Number(data.maximum_lease_months) : null,
      deposit_amount: data.deposit_amount ? Number(data.deposit_amount) : null,
      maintenance_fee: data.maintenance_fee ? Number(data.maintenance_fee) : null,
      
      // Location
      latitude: data.latitude ? Number(data.latitude) : null,
      longitude: data.longitude ? Number(data.longitude) : null,
      address_line1: data.address_line1 || null,
      address_line2: data.address_line2 || null,
      city: data.city || null,
      island: data.island || null,
      postal_code: data.postal_code || null,
      
      // Virtual tour
      virtual_tour_url: data.virtual_tour_url || null,
      
      // Status & flags
      is_featured: data.is_featured === true ? 1 : 0,
      is_new: data.is_new === true ? 1 : 0
    };
    
    console.log("Submitting payload:", payload);
    createProperty.mutate(payload);
  }

  return (
    <div className="relative bg-white dark:bg-gray-900 rounded-xl overflow-hidden">
      {/* Header with close button */}
      <div className="sticky top-0 z-50 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Create New Listing
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Form Content */}
      <div className="max-h-[calc(100vh-200px)] overflow-y-auto p-6">
        <MyMultiStepFormBuilder
          schema={addPropertySchema}
          onFinalSubmit={handleFinalSubmit}
          onDataChange={(data) => {
            console.log("Form data changed:", data);
            setFormData(data);
          }}
          onFormReady={(methods) => {
            console.log("Form ready, setting up methods");
            formMethodsRef.current = methods;
            // Set default values
            methods.setValue("type", "rental");
            methods.setValue("property_category", "residential");
            methods.setValue("property_status", "available");
            methods.setValue("furnished", "unfurnished");
            methods.setValue("price_unit", "night");
            methods.setValue("stock", 1);
            methods.setValue("reserved", 0);
            methods.setValue("low_stock_threshold", 5);
            methods.setValue("reorder_point", 10);
            methods.setValue("country", "MV");
            methods.setValue("island", "Malé");
            methods.setValue("is_new", true);
            methods.setValue("is_featured", false);
          }}
        />
      </div>

      {/* Debug view - remove in production */}
      {process.env.NODE_ENV === "development" && (
        <div className="mt-4 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="text-sm font-mono">
            <details>
              <summary className="cursor-pointer font-semibold mb-2 text-gray-700 dark:text-gray-300">
                Form Data (Debug) - {Object.keys(formData).length} fields
              </summary>
              <pre className="text-xs overflow-auto max-h-96 text-gray-600 dark:text-gray-400 p-2 bg-gray-50 dark:bg-gray-900 rounded">
                {JSON.stringify(formData, null, 2)}
              </pre>
            </details>
          </div>
        </div>
      )}
    </div>
  );
}